<template>
  <div id="people-modal-parent">
    <Page :title="name" v-if="stats">
      <template v-slot:action>
        <div class="stats-sub-header">
          <span class="uk-margin-small-right">最終受検日:</span>
          <span class="uk-text-bold uk-margin-large-right">{{ stats.lastExamDate }}</span>
          <HelpIcon
            tooltipText="回答に矛盾があるかどうかを表します<br />20以下は要注意です"
            position="top-left"
            cls="uk-active uk-width-auto"
          />
          <span class="uk-margin-small-right">信頼係数:</span>
          <span class="uk-text-large uk-text-bold uk-margin-large-right">{{ stats.Reliability }}</span>
        </div>
        <ActionButton
          v-if="showProgressComparisonButton"
          title="前回との比較"
          color="primary"
          shape="round"
          width="150"
          class="uk-margin-small-right"
          @click="onProgressComparisonButtonClick"
        />
        <ActionButton
          v-if="!printData"
          title="ワークシート"
          color="primary"
          shape="round"
          width="150"
          class="uk-margin-small-right"
          @click="onWorksheetButtonClick"
        />
        <ActionButton title="印刷" v-if="!printData" color="primary" shape="round" width="150" @click="onPrintButtonClick" />
      </template>
      <template v-slot:content>
        <div class="area">
          <div class="uk-card uk-card-default uk-card-body personality-detail">
            <span class="title">性格・パーソナリティ</span>
            <HelpIcon
              tooltipText="成人すると、ほとんど変化が見られないものです<br />数値が低くても高くても、強み弱みが存在します"
              position="top-left"
              cls="uk-active uk-width-auto"
            />
            <div class="content">
              <table class="gauge-chart">
                <tbody v-for="(type, i) in uniqueTypes()" :key="i">
                  <tr>
                    <td :rowspan="rowCount(type)" class="shrink">
                      {{ $t(type) }}
                    </td>
                    <td class="shrink">{{ rowEvaluation(rowItems(type)[0]) }}{{ $t(rowItems(type)[0].aspect) }}</td>
                    <td>
                      <GaugeChart
                        :split="4"
                        :chartData="{ value: rowItems(type)[0].value }"
                        :tooltip="rowItems(type)[0].value > 49 ? rowItems(type)[0].tooltip.high : rowItems(type)[0].tooltip.low"
                      />
                    </td>
                  </tr>
                  <tr v-for="n of rowCount(type) - 1" :key="n">
                    <td class="shrink">{{ rowEvaluation(rowItems(type)[n]) }}{{ $t(rowItems(type)[n].aspect) }}</td>
                    <td>
                      <GaugeChart
                        :split="4"
                        :chartData="{ value: rowItems(type)[n].value }"
                        :tooltip="rowItems(type)[n].value > 49 ? rowItems(type)[n].tooltip.high : rowItems(type)[n].tooltip.low"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="descriptions">
                <ul>
                  <li
                    class="description"
                    v-for="(item, i) in stats.PersonalityDetail.description"
                    :key="i"
                    v-html="item.replace(/\n/g, '<br/>')"
                  ></li>
                </ul>
              </div>
            </div>
          </div>
          <div class="uk-card uk-card-default uk-card-body">
            <span class="title">ヒューマンスキル</span>
            <HelpIcon
              tooltipText="AIで代替えすることができない人間本来の本質的な対人関係能力です"
              position="top-left"
              cls="uk-active uk-width-auto"
            />
            <div class="content">
              <HumanSkillChart :chartData="stats.HumanSkill" />
            </div>
          </div>
        </div>
        <div class="area page-break">
          <div class="uk-card uk-card-default uk-card-body">
            <span class="title">社会性・行動特性</span>
            <HelpIcon
              tooltipText="過去の経験や、置かれている状況、与えられたポジションなどにより変化します<br />数値が低くても高くても、強み弱みが存在します"
              position="top-left"
              cls="uk-active uk-width-auto"
            />
            <div class="content">
              <table class="gauge-chart">
                <tbody v-for="(item, i) in stats.Sociality" :key="i">
                  <tr>
                    <td class="property" rowspan="2">{{ rowEvaluation(item) }}{{ item.name }}</td>
                    <td>
                      <GaugeChart
                        :split="4"
                        :chartData="{ value: item.value }"
                        :tooltip="item.tooltip ? (item.value > 49 ? item.tooltip.high : item.tooltip.low) : ''"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td class="description">
                      {{ item.description || "&nbsp;" }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="uk-card uk-card-default uk-card-body">
            <span class="title">カルチャーフィット</span>
            <HelpIcon
              tooltipText="どのような組織文化を求めているかを表します"
              position="top-left"
              cls="uk-active uk-width-auto"
            />
            <CulturalFitChart :chartData="stats.CulturalFit" />
          </div>
        </div>
        <div class="area page-break">
          <div class="uk-card uk-card-default uk-card-body activity-motivation-detail">
            <span class="title">意欲・ヤル気</span>
            <HelpIcon
              tooltipText="過去の経験や、置かれている状況、与えられたポジションなどにより変化します<br />数値が低くても高くても、強み弱みが存在します"
              position="top-left"
              cls="uk-active uk-width-auto"
            />
            <div class="content">
              <table class="gauge-chart">
                <tbody v-for="(item, i) in stats.ActivityMotivationDetail.items" :key="i">
                  <tr>
                    <td class="property" rowspan="2">{{ rowEvaluation(item) }}{{ item.name }}</td>
                    <td>
                      <GaugeChart
                        :split="4"
                        :chartData="{ value: item.value }"
                        :tooltip="item.tooltip ? (item.value > 49 ? item.tooltip.high : item.tooltip.low) : ''"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td class="description">
                      {{ item.description || "&nbsp;" }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="descriptions" v-html="stats.ActivityMotivationDetail.description"></div>
            </div>
          </div>
          <div class="uk-card uk-card-default uk-card-body behavior">
            <span class="title">接し方</span>
            <HelpIcon
              tooltipText="本人の意欲や姿勢の度合いによって、どう接したらいいのか可視化します<br />青丸が今回結果を、赤丸が前回結果を示します"
              position="top-left"
              cls="uk-active uk-width-auto"
            />
            <div class="content">
              <BehaviorChart :chartDataCurrent="stats.Behavior" :chartDataLast="{x:lastBehaviorX, y:lastBehaviorY, isDisp:lastBehaviorDisplay}"/>
            </div>
          </div>
        </div>
        <div class="area page-break">
          <div class="uk-card uk-card-default uk-card-body self-evaluation">
            <span class="title">自己評価</span>
            <HelpIcon
              tooltipText="自分への自信度を表します<br />自己評価と、受検結果の矛盾点を確認できます"
              position="top-left"
              cls="uk-active uk-width-auto"
            />
            <div class="content">
              <div v-for="(item, i) in stats.SelfEvaluation" :key="i" class="self-evaluation-item">
                <table>
                  <tbody>
                    <tr>
                      <th class="major-class-name" :colspan="item.minorClasses.length">
                        {{ item.majorClass.name }}
                      </th>
                    </tr>
                    <tr>
                      <td v-for="(minorItem, j) in item.minorClasses" :key="j" class="minor-class-name">
                        <span>{{ minorItem.name }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td v-for="(minorItem, j) in item.minorClasses" :key="j" class="minor-class-value">
                        {{ minorItem.value }}
                      </td>
                    </tr>
                    <tr>
                      <td class="major-class-value" :colspan="item.minorClasses.length">
                        {{ item.majorClass.value }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="uk-card uk-card-default uk-card-body similarity" v-if="stats.Similarity">
            <span class="title">近いタイプの人</span>
            <HelpIcon
              tooltipText=" 社内の誰にタイプ(性格 / 行動・意欲)が似ているかわかります"
              position="top-left"
              cls="uk-active uk-width-auto"
            />
            <div class="content">
              <template v-if="stats.Similarity.length">
                <div v-for="(item, i) in stats.Similarity" :key="i" class="similarity-item">
                  <span class="similarity-name">{{ item.name }}</span>
                  <ul>
                    <li v-for="(item, j) in item.people" :key="j" class="similarity-people">
                      <span><Avatar :clickable="false" :image="item.image"/></span>
                      <span class="people-name">{{ item.lastName }} {{ item.firstName }}</span>
                      <span class="similarity-value">{{ item.value }}</span>
                    </li>
                  </ul>
                </div>
              </template>
              <template v-else>
                <span>ただいま計算中です</span>
              </template>
            </div>
          </div>
        </div>
        <div class="area">
          <div class="uk-card uk-card-default uk-card-body interview-tips">
            <span class="title">面談時のポイント</span>
            <div class="content">
              <div class="tip" v-for="(item, i) in stats.InterviewTips" :key="i">
                <div class="tip-content" :row="item" v-html="item"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="area page-break">
          <div class="column">
            <div class="uk-card uk-card-default uk-card-body overall-judgement">
              <span class="title">判定結果</span>
              <HelpIcon
                tooltipText=" 落ち着き度合いを測っており、マイナスのほうが落着きがない、プラスのほうが落ち着きがあると読みます<br />(-35~+35で評価。営業系はマイナス、事務系はプラスになる傾向にあります。)"
                position="top-left"
                cls="uk-active uk-width-auto"
              />
              <div class="card-content">
                <div class="content">
                  <table class="gauge-chart">
                    <tbody>
                      <tr>
                        <td class="property" rowspan="2">定着性・安定性</td>
                        <td>
                          <StabilityGaugeChart :chartData="{ value: stats.Judgement.overall.stability }" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="evaluation">
                  <div class="score">
                    <label class="uk-margin-small-right">ゼネラリスト指向</label>
                    <div
                      v-for="item in overallJudgementScores"
                      :key="item"
                      :class="{
                        active: item === stats.Judgement.overall.evaluation
                      }"
                    >
                      {{ item }}
                    </div>
                    <label class="uk-margin-small-left">スペシャリスト指向</label>
                  </div>
                </div>
              </div>
            </div>
            <div class="no-decoration">
              <div class="uk-card uk-card-default uk-card-body tendency">
                <span class="title">傾向</span>
                <div class="card-content">
                  <div v-for="(item, i) in stats.Judgement.tendency" :key="i" class="level">
                    <div>{{ item.left }}</div>
                    <div>
                      <LevelChart :split="2" :chartData="{ value: item.value }" />
                    </div>
                    <div>{{ item.right }}</div>
                  </div>
                </div>
              </div>
              <div class="uk-card uk-card-default uk-card-body job-aptitude">
                <span class="title">適性職務予測推定値</span>
                <div class="content">
                  <table>
                    <tbody>
                      <tr v-for="(item, i) in stats.Judgement.jobAptitude" :key="i">
                        <td class="gauge-title">{{ item.name }}</td>
                        <td class="gauge">
                          <GaugeChart :split="4" :chartData="{ value: item.value }" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="uk-card uk-card-default uk-card-body assignment-aptitude">
                <span class="title">職業興味領域</span>
                <HelpIcon
                  tooltipText="自分に適性と感じる職務が見当たらない人のために、おおよその方向性を示唆しています"
                  position="top-left"
                  cls="uk-active uk-width-auto"
                />
                <div class="content">
                  <ul>
                    <li v-for="(item, i) in stats.Judgement.jobInterest" :key="i">
                      {{ item }}
                    </li>
                  </ul>
                </div>
              </div>
              <div class="uk-card uk-card-default uk-card-body">
                <span class="title">テクニカルスキル</span>
                <HelpIcon
                  tooltipText="年齢、雇用形態を問わず社会人として必要とされる汎用的な能力です</br>(知識力、技能力、思考力、対人力、マネジメント力)"
                  position="top-left"
                  cls="uk-active uk-width-auto"
                />
                <TechnicalSkill :isTop="{isTisikiTop: isTisikiTop, isGinouTop: isGinouTop, isSikouTop: isSikouTop, isTaizinTop: isTaizinTop, isManagementTop: isManagementTop}"/>
              </div>
            </div>
          </div>
          <div class="column">
            <div class="uk-card uk-card-default uk-card-body assignment-aptitude">
              <span class="title">部署適正（全体）</span>
              <HelpIcon tooltipText="実務向きか、管理向きかを表します" position="top-left" cls="uk-active uk-width-auto" />
              <div class="content">
                <div class="high">
                  <span class="assignment-aptitude-title">適性の高い順</span>
                  <ul>
                    <li v-for="(item, i) in stats.Judgement.assignmentAptitude1.high" :key="i">
                      {{ item }}
                    </li>
                  </ul>
                </div>
                <div class="low">
                  <span class="assignment-aptitude-title">適性の低い順</span>
                  <ul>
                    <li v-for="(item, i) in stats.Judgement.assignmentAptitude1.low" :key="i">
                      {{ item }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="uk-card uk-card-default uk-card-body assignment-aptitude">
              <span class="title">職種適正（全体）</span>
              <HelpIcon
                tooltipText="51職種の職種の中から高い順、低い順に表示します"
                position="top-left"
                cls="uk-active uk-width-auto"
              />
              <div class="content">
                <div class="high">
                  <span class="assignment-aptitude-title">適性の高い順</span>
                  <ul>
                    <li v-for="(item, i) in stats.Judgement.assignmentAptitude2.high" :key="i">
                      {{ item }}
                    </li>
                  </ul>
                </div>
                <div class="low">
                  <span class="assignment-aptitude-title">適性の低い順</span>
                  <ul>
                    <li v-for="(item, i) in stats.Judgement.assignmentAptitude2.low" :key="i">
                      {{ item }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="uk-card uk-card-default uk-card-body assignment-aptitude">
              <span class="title">職種適正（自社）</span>
              <HelpIcon
                tooltipText="自社に設定されている職種の中から高い順、低い順に表示します"
                position="top-left"
                cls="uk-active uk-width-auto"
              />
              <div class="content">
                <div class="high">
                  <span class="assignment-aptitude-title">適性の高い順</span>
                  <template v-if="!stats.Judgement.assignmentAptitude3.high.length">
                    <p>従業員の職種に該当はありません</p>
                  </template>
                  <ul>
                    <li v-for="(item, i) in stats.Judgement.assignmentAptitude3.high" :key="i">
                      {{ item }}
                    </li>
                  </ul>
                </div>
                <div class="low">
                  <span class="assignment-aptitude-title">適性の低い順</span>
                  <template v-if="!stats.Judgement.assignmentAptitude3.low.length">
                    <p>従業員の職種に該当はありません</p>
                  </template>
                  <ul>
                    <li v-for="(item, i) in stats.Judgement.assignmentAptitude3.low" :key="i">
                      {{ item }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Page>
    <div class="uk-flex-top" uk-modal ref="modal" container="#people-modal-parent" v-if="showProgressComparisonButton">
      <div class="uk-modal-dialog uk-margin-auto-vertical">
        <div class="uk-modal-body">
          <ProgressChart
            :id="progressChartId"
            :height="100"
            :chartData="progressChartData"
            :options="progressChartOptions"
            :hideScoreDiff="true"
            :ref="progressChartId"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.area {
  display: flex;
  margin-bottom: $spacing-ex-small;
  color: $brand-color;
  font-weight: bold;
  & > div {
    color: $brand-color;
    flex: 1;
    margin: 0 $spacing-small;
    &:first-child {
      margin-left: 0;
    }
    &:last-child {
      margin-right: 0;
    }
  }
  .uk-card {
    border: 1px solid $border-color-secondary;
    border-radius: 3px;
    padding: $spacing-ex-small $spacing-medium;
  }
  .column {
    .uk-card {
      margin-top: $spacing-ex-small;
    }
  }
}
.area .title {
  color: $brand-color;
}
table.gauge-chart {
  width: 100%;
  td.shrink {
    width: 1%;
    white-space: nowrap;
  }
  td .container {
    margin-top: -5px;
  }
  td.property {
    vertical-align: top;
  }
  td.property + td {
    width: 80%;
  }
  td.description {
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-size: 0.7rem;
    white-space: nowrap;
    padding-bottom: $spacing-ex-small;
  }
}
.personality-detail {
  td {
    padding: $spacing-minimum;
  }
}
@mixin content {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: $spacing-ex-small;
}
.uk-card-body > .content {
  @include content;
}
.personality-detail {
  .content {
    flex-direction: column;
  }
  .descriptions {
    width: 100%;
  }
  .description {
    font-size: 0.78rem;
    padding: $spacing-minimum;
  }
}
.behavior {
  .content {
    padding-bottom: $spacing-small;
  }
}
.similarity {
  .content {
    ul {
      flex: 1;
    }
    .similarity-people {
      margin-bottom: $spacing-small;
      font-size: 0.8em;
      display: flex;
      align-items: center;
      .people-name {
        margin-left: $spacing-small;
        flex: 1;
      }
      .similarity-value {
        margin-left: $spacing-ex-small;
        width: 55px;
      }
    }
  }
}
.self-evaluation {
  .content {
    padding: $spacing-ex-small;
    table {
      border-collapse: collapse;
    }
    .self-evaluation-item {
      border-collapse: collapse;
      border: 1px solid;
      border-right: 0;
      &:last-child {
        border-right: 1px solid;
      }
    }
    .major-class-name {
      border-bottom: 1px solid;
    }
    td,
    th {
      text-align: center;
      padding: $spacing-small;
    }
    td:not(.major-class-value) {
      border-right: 1px solid;
      border-bottom: 1px solid;
    }
    .minor-class-name {
      width: 24px;
      height: 120px;
      &:last-child {
        border-right: none;
      }
      span {
        writing-mode: vertical-rl;
        min-height: 60px;
        height: 120px;
      }
    }
    .minor-class-value {
      &:last-child {
        border-right: none;
      }
    }
  }
}
.interview-tips {
  .content {
    margin-top: $spacing-small;
    display: flex;
    flex-wrap: wrap;
    justify-content: initial;
    .tip {
      border: 1px solid;
      padding: $spacing-minimum;
      margin: $spacing-minimum;
      width: calc(50% - 2px - #{$spacing-minimum * 4});
      min-height: 63px;
    }
    .tip-content {
      font: 0.875rem/1.5 Consolas, monaco, monospace;
      color: #666;
      border: 0;
      white-space: pre-wrap;
      padding: 0;
      margin: 0;
    }
  }
}
.tendency {
  .card-content {
    > div:last-child {
      :deep(.chart) {
        border-bottom-width: 1px;
      }
    }
    flex-direction: column;
    @include content;
  }
  .level {
    display: flex;
    width: 100%;
    margin-top: -1px;
    align-items: center;
    font-size: 0.9rem;
    div {
      flex: 1;
      &:first-child {
        text-align: right;
      }
      &:nth-child(2) {
        flex: 3;
        margin: 0 $spacing-ex-small;
      }
    }
    :deep(.chart) {
      border-bottom-width: 0;
    }
  }
}
.job-aptitude {
  .content {
    flex-direction: column;
    .gauge-title {
      font-size: 0.9rem;
    }
    table {
      width: 100%;
      td:first-child {
        width: 1%;
        white-space: nowrap;
      }
      td .container {
        margin-top: -5px;
      }
    }
    tbody {
      > tr:last-child {
        :deep(.chart) {
          border-bottom-width: 1px;
        }
        :deep(.score) {
          border-bottom-width: 1px;
        }
      }
    }
    :deep(.chart) {
      border-bottom-width: 0;
    }
    :deep(.score) {
      border-bottom-width: 0;
    }
  }
}
.assignment-aptitude {
  div.content {
    margin: 0 $spacing-ex-small;
    justify-content: space-evenly;
    align-items: flex-start;
    ul {
      flex: 1;
      padding: 0;
    }
    li {
      list-style: none;
      font-size: 0.9rem;
    }
  }
}
.overall-judgement {
  flex-direction: column;
  font-size: 0.9rem;
}
.overall-judgement .card-content {
  padding: $spacing-ex-small;
  .stability {
    display: flex;
    margin-bottom: $spacing-ex-small;
    line-height: 2.5;
  }
  .evaluation {
    margin-top: 20px;
    .score {
      display: flex;
      justify-content: center;
      align-items: center;
      margin: $spacing-small 0;
      div {
        border: 1px solid $border-color-secondary;
        border-right: 0;
        width: 42px;
        height: 42px;
        display: flex;
        justify-content: center;
        align-items: center;
        &:nth-last-child(2) {
          border-right: 1px solid $border-color-secondary;
        }
        &.active {
          background-color: $brand-color !important;
          color: #fff;
        }
      }
    }
    .instruction {
      display: flex;
      justify-content: space-between;
    }
  }
}
.activity-motivation-detail {
  .content {
    flex-direction: column;
  }
  .descriptions {
    width: 100%;
    font-size: 0.8rem;
    padding: $spacing-small;
  }
}
.hr-judgment-choice {
  position: relative;
  min-height: 250px;
  .judgment-choice-list {
    margin: $spacing-ex-small $spacing-small;
  }
  .judgment-choice-button {
    bottom: $spacing-ex-small;
  }
}
.hr-judgment-result {
  min-height: 250px;
}
.uk-modal-dialog {
  width: unset;
  min-width: 960px;
}
</style>
<script lang="ts">
import { Vue, Options } from "vue-class-component";
import { Prop, Ref } from "vue-property-decorator";
import UIkit from "uikit";
import Page from "../molecules/Page.vue";
import Store from "../../store";
import HelpIcon from "../atoms/HelpIcon.vue";
import ActionButton from "../atoms/ActionButton.vue";
import { ClientApi, PersonalityDetailItem, UserStats, InstructionLevel } from "../../types";
import { StatsApi, StatsItemEnum, GetStat } from "../../autogen";
import { print } from "../../helpers/view-utils";
import LevelChart from "../atoms/LevelChart.vue";
import GaugeChart from "../atoms/GaugeChart.vue";
import StabilityGaugeChart from "../atoms/StabilityGaugeChart.vue";
import HumanSkillChart from "../atoms/HumanSkillChart.vue";
import Avatar from "../atoms/Avatar.vue";
import CulturalFitChart from "../molecules/CulturalFitChart.vue";
import BehaviorChart from "../atoms/BehaviorChart.vue";
import ProgressChart from "../molecules/ProgressChart.vue";
import TechnicalSkill from "../atoms/TechnicalSkill.vue";
import * as RoleHelper from "../../helpers/role-helper";
import { PersonalityDetailGroupTooltip, SocialityTooltip, ActivityMotivationDetailTooltip } from "../../helpers/const";

type PersonalityDetailGroupItems = { [key: string]: PersonalityDetailItem[] };
declare module "@vue/runtime-core" {
  export interface ComponentCustomProperties {
    $role: typeof RoleHelper;
  }
}

type ChartData = {
  examDate: string[];
  examDateOptions: Object;
  datas: Object;
};
type DataSet = {
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  data: Object[];
  label: string;
};
type GraphData = {
  labels: string[] | string[][];
  datasets: DataSet[];
};

@Options({
  components: {
    Page,
    HelpIcon,
    ActionButton,
    LevelChart,
    GaugeChart,
    Avatar,
    CulturalFitChart,
    HumanSkillChart,
    BehaviorChart,
    ProgressChart,
    StabilityGaugeChart,
    TechnicalSkill
  }
})
export default class People extends Vue {
  globalErrors: { message: string }[] = [];
  name?: string = "";
  stats: GetStat | null = null;
  overallJudgementScores = ["A", "B", "C", "D", "E"];
  personalityDetailGroup: PersonalityDetailGroupItems = {};
  peopleId = "";
  changeable = true;
  showProgressComparisonButton = false;
  readonly progressChartId = "PROGRESS_COMPARISON_CHART";
  colors: string[] = ["rgba(255, 128, 140, 1)", "rgba(128, 140, 255, 1)"];
  progressChartData: ChartData | null = null;
  progressChartOptions = {
    interaction: {
      intersect: false,
      mode: "index"
    },
    scales: {
      y: {
        suggestedMin: 10,
        suggestedMax: 90
      }
    },
    plugins: {
      tooltip: {
        intersect: false,
        callbacks: {
          title: (tooltipItems: any) => {
            const title = tooltipItems[0].label.replaceAll(",", "").replaceAll("丨", "ー");
            return title;
          }
        }
      }
    }
  };
  lastBehaviorDisplay = false;
  lastBehaviorX = 0;
  lastBehaviorY = 0;
  isTisikiTop = false;
  isGinouTop = false;
  isSikouTop = false;
  isTaizinTop = false;
  isManagementTop = false;
  @Ref("modal") modal!: HTMLElement;
  @Prop({ type: Object }) printData?:
    | (GetStat & {
        name: string;
      })
    | null = null;
  @Prop({ type: Object }) options = {};
  @Prop({ type: Object }) statsItems? = [];
  uniqueTypes() {
    return Object.keys(this.personalityDetailGroup);
  }
  rowCount(type: string) {
    return this.personalityDetailGroup[type].length;
  }
  rowItems(type: string) {
    return this.personalityDetailGroup[type];
  }
  rowEvaluation(row: PersonalityDetailItem) {
    return row.value <= 20 ? "▲" : row.value >= 80 ? "●" : "";
  }
  async beforeMount() {
    if (this.printData) {
      this.stats = (this.printData as unknown) as UserStats;
      this.name = this.printData.name;
      this.changeable = false;
      let examDates = this.stats.ExamDate!.examDates!.map(date => this.toDateFormat(date)) || [];
      // 接し方座標 (前回位置)
      if (examDates.length >= 2) {
        this.lastBehaviorDisplay = true;
        let dataA = this.stats;           // 受験者のスタッツ全体
        let dataB = dataA.Progress;       // 受験者のスタッツから経年データを取得
        if (dataB !== undefined) {
          let dataC = dataB[1];           // 経年データの最新から1つ遡る(前回データ)
          if (dataC !== undefined) {
            let dataD = dataC.score;      // 前回データの各種スコア
            if (dataD !== undefined) {
              let zyuuzyunnSei = dataD[27].value;
              let zisyuSei = dataD[28].value;
              let tasseiYokkyuu = dataD[30].value;
              let ziritsuYokkyuu = dataD[31].value;
              let kyuutiYokkyuu = dataD[32].value;
              let kikiTaisei = dataD[33].value;
              let kinrouIyoku = dataD[34].value;
              this.lastBehaviorX = (Number(tasseiYokkyuu) + Number(ziritsuYokkyuu) + Number(kyuutiYokkyuu) + Number(kikiTaisei) + Number(kinrouIyoku)) / 5;
              this.lastBehaviorY = (Number(zisyuSei) + (100 - Number(zyuuzyunnSei))) / 2;
            }
          }
        }
      }
      // テクニカルスキル
      let baseStats = this.stats;
      let baseProgress = baseStats.Progress;
      if (baseProgress !== undefined) {
        let currentProgress = baseProgress[0];
        if (currentProgress !== undefined) {
          let baseScore = currentProgress.score;
          if (baseScore !== undefined) {
            // 必要な値を抽出
            let zizokusei = Number(baseScore[14].value);
            let kisokusei = Number(baseScore[15].value);
            let kyakkansei = Number(baseScore[11].value);
            let sekkyokusei = Number(baseScore[20].value);
            let zyuuzyunnsei = Number(baseScore[27].value);
            let zisyusei = Number(baseScore[28].value);
            let kyuutiYokkyuu = Number(baseScore[32].value);
            let yokubou = Number(baseScore[39].value);
            let sekininkan = Number(baseScore[22].value);
            let sidousei = Number(baseScore[24].value);
            let tasseiYokkyuu = Number(baseScore[30].value);
            let kyoutyousei = Number(baseScore[21].value);
            let tituzyo = Number(baseScore[38].value);
            let sinwa = Number(baseScore[37].value);
            let kyoukansei = Number(baseScore[25].value);
            let sihai = Number(baseScore[36].value);
            let antei = Number(baseScore[26].value);

            // テクニカルスキルの値を算出
            let tisiki = (zizokusei + kisokusei + sekkyokusei + zyuuzyunnsei + zisyusei + kyuutiYokkyuu + yokubou) / 7;
            let ginou = (sekkyokusei + zisyusei + sekininkan + sidousei + tasseiYokkyuu) / 5;
            let sikou = ((100 - kyuutiYokkyuu) + tasseiYokkyuu + kyoutyousei + tituzyo) / 4;
            let taizin =  ((100 - kisokusei) + kyakkansei + sekkyokusei + kyoutyousei + sinwa + kyoukansei + sihai) / 7;
            let management = ((100 - kisokusei) + kyakkansei + sekkyokusei + sidousei + kyoutyousei + sinwa + kyoukansei + antei) / 8;

            // 最高値のものを抽出
            let max = 0.0;
            max = tisiki;
            this.isTisikiTop = true;

            if (max == ginou) {
              this.isGinouTop = true;
            } else if (max < ginou) {
              this.isTisikiTop = false;
              this.isGinouTop = true;
              max = ginou;
            }

            if (max == sikou) {
              this.isSikouTop = true;
            } else if (max < sikou) {
              this.isTisikiTop = false;
              this.isGinouTop = false;
              this.isSikouTop = true;
              max = sikou;
            }

            if (max == taizin) {
              this.isTaizinTop = true;
            } else if (max < taizin) {
              this.isTisikiTop = false;
              this.isGinouTop = false;
              this.isSikouTop = false;
              this.isTaizinTop = true;
              max = taizin;
            }

            if (max == management) {
              this.isManagementTop = true;
            } else if (max < management) {
              this.isTisikiTop = false;
              this.isGinouTop = false;
              this.isSikouTop = false;
              this.isTaizinTop = false;
              this.isManagementTop= true;
              max = management;
            }
          }
        }
      }
    } else {
      const paths = this.$route.path.split("/");
      const peopleType = paths[paths.length - 2];
      const isEmployee = peopleType === "employees";
      const prefix = isEmployee ? "EMP" : "APL";
      const suffix = this.$route.params.suffix && this.$route.params.suffix.length ? this.$route.params.suffix : "";
      const rawPeopleId = this.$route.params["peopleId"] as string;
      // EMP#1#RetiredのようなSKに変換
      const peopleId = (this.peopleId = `${prefix}#${rawPeopleId}#${suffix}`);
      const response = await new StatsApi().getPeopleStats({
        peopleIds: [peopleId],
        statsItems: this.statsItems?.length
          ? this.statsItems
          : [
              StatsItemEnum.CulturalFit,
              StatsItemEnum.HumanSkill,
              StatsItemEnum.PersonalityDetail,
              StatsItemEnum.Sociality,
              StatsItemEnum.ActivityMotivationDetail,
              StatsItemEnum.Behavior,
              StatsItemEnum.SelfEvaluation,
              StatsItemEnum.SimilarType,
              StatsItemEnum.InterviewTips,
              StatsItemEnum.Reliability,
              StatsItemEnum.Judgement,
              StatsItemEnum.JobInterest,
              StatsItemEnum.LastExamDate,
              StatsItemEnum.ExamDate,
              StatsItemEnum.Progress
            ]
      });
      const data = response.data as UserStats;
      if (!Object.keys(data).length) {
        alert("未受検または受検結果処理中のためデータを表示できません");
        this.$router.go(-1);
        return;
      }
      this.stats = data[peopleId].stats;

      this.name = `${data[peopleId].profile.lastName} ${data[peopleId].profile.firstName}`;
      const behavior = this.stats["Behavior"]!;
      behavior.x = behavior.x > 70 ? 70 : behavior.x < 30 ? 30 : behavior.x;
      behavior.y = behavior.y > 70 ? 70 : behavior.y < 30 ? 30 : behavior.y;

      let examDates = this.stats.ExamDate!.examDates!.map(date => this.toDateFormat(date)) || [];
      this.showProgressComparisonButton = examDates.length >= 2;
      this.progressChartData = (examDates
        ? {
            examDate: examDates,
            examDateOptions: {
              mode: "tags",
              value: [],
              label: "label",
              trackBy: "value",
              closeOnSelect: false,
              options: examDates.map(date => ({ value: date, label: `${date} 時` })),
              reachable: true,
              createOption: false,
              delay: 0
            },
            datas: {
              labels: [],
              datasets: []
            }
          }
        : {}) as ChartData;

      const targetStats = (data as UserStats)![this.peopleId].stats["Progress"];
      if (targetStats && targetStats.length > 1) {
        const laterProgresses = targetStats.sort((a, b) => (a.date > b.date ? -1 : 1)).slice(0, 2);
        (this.progressChartData!.datas as GraphData).labels = laterProgresses[0].score.map(score =>
          score.name
            .replace(/ー/g, "丨")
            .replace("ﾓﾗﾄﾘｱﾑ", "モラトリアム")
            .split("")
        );
        (this.progressChartData!.datas as GraphData).datasets = laterProgresses.map((progress, index) => ({
          label: `${this.toDateFormat(progress.date)} 時`,
          data: progress.score.map(score => Math.round(score.value)),
          backgroundColor: this.colors[index],
          borderColor: this.colors[index],
          borderWidth: 2
        }));
      }
      // 接し方座標 (前回位置)
      if (examDates.length >= 2) {
        this.lastBehaviorDisplay = true;
        let dataA = this.stats;           // 受験者のスタッツ全体
        let dataB = dataA.Progress;       // 受験者のスタッツから経年データを取得
        if (dataB !== undefined) {
          let dataC = dataB[1];           // 経年データの最新から1つ遡る(前回データ)
          if (dataC !== undefined) {
            let dataD = dataC.score;      // 前回データの各種スコア
            if (dataD !== undefined) {
              let zyuuzyunnSei = dataD[27].value;
              let zisyuSei = dataD[28].value;
              let tasseiYokkyuu = dataD[30].value;
              let ziritsuYokkyuu = dataD[31].value;
              let kyuutiYokkyuu = dataD[32].value;
              let kikiTaisei = dataD[33].value;
              let kinrouIyoku = dataD[34].value;
              this.lastBehaviorX = (Number(tasseiYokkyuu) + Number(ziritsuYokkyuu) + Number(kyuutiYokkyuu) + Number(kikiTaisei) + Number(kinrouIyoku)) / 5;
              this.lastBehaviorY = (Number(zisyuSei) + (100 - Number(zyuuzyunnSei))) / 2;
            }
          }
        }
      }
      // テクニカルスキル
      let baseStats = this.stats;
      let baseProgress = baseStats.Progress;
      if (baseProgress !== undefined) {
        let currentProgress = baseProgress[0];
        if (currentProgress !== undefined) {
          let baseScore = currentProgress.score;
          if (baseScore !== undefined) {
            // 必要な値を抽出
            let zizokusei = Number(baseScore[14].value);
            let kisokusei = Number(baseScore[15].value);
            let kyakkansei = Number(baseScore[11].value);
            let sekkyokusei = Number(baseScore[20].value);
            let zyuuzyunnsei = Number(baseScore[27].value);
            let zisyusei = Number(baseScore[28].value);
            let kyuutiYokkyuu = Number(baseScore[32].value);
            let yokubou = Number(baseScore[39].value);
            let sekininkan = Number(baseScore[22].value);
            let sidousei = Number(baseScore[24].value);
            let tasseiYokkyuu = Number(baseScore[30].value);
            let kyoutyousei = Number(baseScore[21].value);
            let tituzyo = Number(baseScore[38].value);
            let sinwa = Number(baseScore[37].value);
            let kyoukansei = Number(baseScore[25].value);
            let sihai = Number(baseScore[36].value);
            let antei = Number(baseScore[26].value);

            // テクニカルスキルの値を算出
            let tisiki = (zizokusei + kisokusei + sekkyokusei + zyuuzyunnsei + zisyusei + kyuutiYokkyuu + yokubou) / 7;
            let ginou = (sekkyokusei + zisyusei + sekininkan + sidousei + tasseiYokkyuu) / 5;
            let sikou = ((100 - kyuutiYokkyuu) + tasseiYokkyuu + kyoutyousei + tituzyo) / 4;
            let taizin =  ((100 - kisokusei) + kyakkansei + sekkyokusei + kyoutyousei + sinwa + kyoukansei + sihai) / 7;
            let management = ((100 - kisokusei) + kyakkansei + sekkyokusei + sidousei + kyoutyousei + sinwa + kyoukansei + antei) / 8;

            // 最高値のものを抽出
            let max = 0.0;
            max = tisiki;
            this.isTisikiTop = true;

            if (max == ginou) {
              this.isGinouTop = true;
            } else if (max < ginou) {
              this.isTisikiTop = false;
              this.isGinouTop = true;
              max = ginou;
            }

            if (max == sikou) {
              this.isSikouTop = true;
            } else if (max < sikou) {
              this.isTisikiTop = false;
              this.isGinouTop = false;
              this.isSikouTop = true;
              max = sikou;
            }

            if (max == taizin) {
              this.isTaizinTop = true;
            } else if (max < taizin) {
              this.isTisikiTop = false;
              this.isGinouTop = false;
              this.isSikouTop = false;
              this.isTaizinTop = true;
              max = taizin;
            }

            if (max == management) {
              this.isManagementTop = true;
            } else if (max < management) {
              this.isTisikiTop = false;
              this.isGinouTop = false;
              this.isSikouTop = false;
              this.isTaizinTop = false;
              this.isManagementTop= true;
              max = management;
            }

          }
        }
      }

      try {
        const criteriaResponse = await new ClientApi().getHrCriteria({});
      } catch (err) {
        if (err.response.status === 403) {
          // skip this status
        } else {
          this.globalErrors = [{ message: err.message }];
        }
      }
    }
    this.personalityDetailGroup = this.stats!.PersonalityDetail!.items.reduce((a, b) => {
      if (!a[b.type]) a[b.type] = [];
      a[b.type].push(b);
      b.tooltip = PersonalityDetailGroupTooltip[b.aspect];
      return a;
    }, {} as PersonalityDetailGroupItems);
    if (this.stats) {
      if (this.stats.Sociality) {
        this.stats.Sociality = this.stats.Sociality.map(sociality => ({
          name: sociality.name,
          value: sociality.value,
          description: sociality.description,
          tooltip: SocialityTooltip[sociality.name as keyof typeof SocialityTooltip]
        }));
      }
      if (this.stats.ActivityMotivationDetail && this.stats.ActivityMotivationDetail.items) {
        this.stats.ActivityMotivationDetail.items = this.stats.ActivityMotivationDetail.items.map(detail => ({
          name: detail.name,
          value: detail.value,
          description: detail.description,
          tooltip: ActivityMotivationDetailTooltip[detail.name as keyof typeof ActivityMotivationDetailTooltip]
        }));
      }
    }
  }
  beforeUnmount() {
    if (this.modal) this.modal.remove();
  }
  async onProgressComparisonButtonClick() {
    UIkit.modal(this.modal).show();
  }
  async onWorksheetButtonClick() {
    Store.getInstance().setIsLoading(true);
    try {
      const response = await new ClientApi().getXlsPersonalWorksheet(
        { personId: this.peopleId },
        { responseType: "arraybuffer" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers["content-type"] }));
      const link = document.createElement("a");
      link.href = url;
      // Filename format: [LastnameFirstName]_ワークシート_[datetime].xlsx
      const filename = `${this.name?.replace(/\s/g, "")}_ワークシート_${new Date().toLocaleString("jp-JP")}`;
      link.setAttribute("download", `${filename}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, "エラーが発生しました");
    } finally {
      Store.getInstance().setIsLoading(false);
    }
  }
  onPrintButtonClick() {
    print(StatsItemEnum.Judgement, Object.assign({ name: this.name }, this.stats));
  }
  toDateFormat(str: string) {
    return `${str.slice(0, 4)}/${str.slice(4, 6)}/${str.slice(6, 8)} ${str.slice(8, 10)}`.trim();
  }
}
</script>
